import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Building,
  Clock,
  CheckCircle,
  XCircle,
  Activity,
} from "lucide-react";
import { fetchUserProfile, STORAGE_KEYS } from "../../services/api";

// User Type Detection
const getUserTypeFlags = async () => {
  try {
    const bpartnerData = localStorage.getItem(STORAGE_KEYS.B_PARTNER);
    if (bpartnerData) {
      const data = JSON.parse(bpartnerData);
      return {
        isBDO: !!data.isbdo,
        isHospital: !!data.isHospital,
        isCollection: !!(data.IsEmployee || data.IsVolunteer),
        isPublicUser: !!(
          data.isdonor &&
          !data.isbdo &&
          !data.isHospital &&
          !(data.IsEmployee || data.IsVolunteer)
        ),
      };
    }
  } catch (err) {
    console.error("Error parsing bpartner data:", err);
  }
  return {
    isBDO: false,
    isHospital: false,
    isCollection: false,
    isPublicUser: false,
  };
};

const Profile = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [userType, setUserType] = useState({ isBDO: false, isHospital: false });

  useEffect(() => {
    loadProfileData();
    loadUserType();
  }, []);

  const loadUserType = async () => {
    try {
      const userTypeFlags = await getUserTypeFlags();
      setUserType(userTypeFlags);
    } catch (err) {
      console.error("Failed to load user type:", err);
    }
  };

  const loadProfileData = async () => {
    try {
      setLoading(true);
      setError(null);

      const profileResponse = await fetchUserProfile();
      setProfileData(profileResponse);
    } catch (err) {
      console.error("Error loading profile data:", err);
      setError(err.message || "Failed to load profile data");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return "N/A";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center mb-2">
            {userType.isHospital ? (
              <User className="h-6 w-6 text-red-600 mr-2" />
            ) : userType.isBDO ? (
              <User className="h-6 w-6 text-green-600 mr-2" />
            ) : null}
            <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
          </div>
          <p className="text-gray-600">
            Your account information and personal details
          </p>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  onClick={() => setError(null)}
                  className="inline-flex bg-red-50 rounded-md p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg
                    className="h-3 w-3"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Header */}
      <div className="card">
        <div className="flex items-center p-6">
          <div className="h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mr-4">
            <User className="h-8 w-8 text-red-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900">
              {profileData?.name || "N/A"}
            </h2>
            <p className="text-gray-600 flex items-center mt-1">
              <Phone className="h-4 w-4 mr-2" />
              {profileData?.phone || "N/A"}
            </p>
          </div>
        </div>
      </div>
      {/* Personal Details Card */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Personal Details
          </h2>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <div className="flex items-center">
                <User className="h-4 w-4 mr-2 text-gray-400" />
                <span className="text-gray-700 font-medium">Name:</span>
              </div>
              <span className="text-gray-900">
                {profileData?.name || "N/A"}
              </span>
            </div>

            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <div className="flex items-center">
                <Mail className="h-4 w-4 mr-2 text-gray-400" />
                <span className="text-gray-700 font-medium">Email:</span>
              </div>
              <span className="text-gray-900">
                {profileData?.email || "N/A"}
              </span>
            </div>

            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <div className="flex items-center">
                <Phone className="h-4 w-4 mr-2 text-gray-400" />
                <span className="text-gray-700 font-medium">Phone:</span>
              </div>
              <span className="text-gray-900">
                {profileData?.phone || "N/A"}
              </span>
            </div>

            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <div className="flex items-center">
                <Building className="h-4 w-4 mr-2 text-gray-400" />
                <span className="text-gray-700 font-medium">
                  {userType.isBDO
                    ? "BDO:"
                    : userType.isHospital
                    ? "Hospital:"
                    : "BP:"}
                </span>
              </div>
              <span className="text-gray-900">
                {profileData?.businessPartner || "N/A"}
              </span>
            </div>

            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                <span className="text-gray-700 font-medium">Location:</span>
              </div>
              <span className="text-gray-900">
                {profileData?.location || "N/A"}
              </span>
            </div>

            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <div className="flex items-center">
                <Clock className="h-4 w-4 mr-2 text-gray-400" />
                <span className="text-gray-700 font-medium">Last Login:</span>
              </div>
              <span className="text-gray-900">
                {formatDate(profileData?.lastLogin)}
              </span>
            </div>

            <div className="flex justify-between items-center py-3">
              <div className="flex items-center">
                {profileData?.isActive ? (
                  <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                ) : (
                  <XCircle className="h-4 w-4 mr-2 text-red-500" />
                )}
                <span className="text-gray-700 font-medium">Status:</span>
              </div>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium">
                {profileData?.isActive ? (
                  <span className="bg-green-100 text-green-800">Active</span>
                ) : (
                  <span className="bg-red-100 text-red-800">Inactive</span>
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
